import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calculator } from "lucide-react";

/**
 * Compact calculator popover for pool water volume (rectangle).
 * Formula: L × W × ((shallow + deep) / 2) × 7.48
 *
 * Props:
 *  - onApply (fn, gallons:number)
 *  - testId (string)
 */
export default function WaterCalculatorPopover({ onApply, testId }) {
  const [open, setOpen] = useState(false);
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [shallow, setShallow] = useState("");
  const [deep, setDeep] = useState("");

  const L = parseFloat(length || 0);
  const W = parseFloat(width || 0);
  const S = parseFloat(shallow || 0);
  const D = parseFloat(deep || 0);
  const valid = L > 0 && W > 0 && S > 0 && D > 0;
  const computed = valid ? Math.round(L * W * ((S + D) / 2) * 7.48) : 0;

  const apply = () => {
    if (!valid) return;
    onApply(computed);
    setOpen(false);
    setLength(""); setWidth(""); setShallow(""); setDeep("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="rounded-sm h-8 w-8 p-0 text-primary"
          data-testid={testId}
          title="Calculate pool water volume"
        >
          <Calculator className="w-3.5 h-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-2" align="end">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-primary" />
          <Label className="text-xs font-semibold uppercase tracking-wider">Pool water (rectangle)</Label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-muted-foreground">Length (ft)</Label>
            <Input type="number" step="0.5" value={length} onChange={(e) => setLength(e.target.value)} className="rounded-sm mono-num h-9" data-testid={`${testId}-length`} />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Width (ft)</Label>
            <Input type="number" step="0.5" value={width} onChange={(e) => setWidth(e.target.value)} className="rounded-sm mono-num h-9" data-testid={`${testId}-width`} />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Shallow (ft)</Label>
            <Input type="number" step="0.5" value={shallow} onChange={(e) => setShallow(e.target.value)} className="rounded-sm mono-num h-9" data-testid={`${testId}-shallow`} />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Deep (ft)</Label>
            <Input type="number" step="0.5" value={deep} onChange={(e) => setDeep(e.target.value)} className="rounded-sm mono-num h-9" data-testid={`${testId}-deep`} />
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-xs">
            <span className="text-muted-foreground">≈ </span>
            <span className="mono-num font-semibold">{valid ? `${computed.toLocaleString()} gal` : "—"}</span>
          </div>
          <Button type="button" size="sm" disabled={!valid} onClick={apply} className="rounded-sm h-8" data-testid={`${testId}-apply`}>
            Apply
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground font-mono">L × W × ((shallow+deep)/2) × 7.48</p>
      </PopoverContent>
    </Popover>
  );
}
